import { JAVA_LINE_BREAK, JS_NOT_SPACE, JS_SPACE, javaIsBlank, javaLines, javaParseDouble, javaRint, javaRound, javaStrip, javaToInt } from '../javaText.ts';
import { writeJson, type JsonValue } from '../jsonWriter.ts';
import { InterchangeError, JavaNumber, javaValueOf, readJavaJson, type JavaJson } from './jsonReader.ts';
import { RollRange } from './rollRange.ts';

/**
 * Reads random tables from CSV, JSON, plain text and Markdown, and writes them back out
 * (docs/PARITY-STEP-2-PLAN.md section 4): a port of the desktop's
 * `interchange.RandomTableCodec`, line for line, so an import gives the same rows, weights
 * and notes in both editions - schema/fixtures/table-import-cases.json holds the desktop's
 * own results. Reading is forgiving, since tables come from spreadsheets, forum posts and
 * PDFs; every interpretation it makes is reported in a table's `notes`.
 */

export type TableFormat = 'CSV' | 'JSON' | 'TEXT' | 'MARKDOWN';
export const TABLE_FORMATS: Readonly<Record<TableFormat, { readonly name: string; readonly extension: string }>> = {
  CSV: { name: 'CSV', extension: 'csv' },
  JSON: { name: 'JSON', extension: 'json' },
  TEXT: { name: 'Plain text', extension: 'txt' },
  MARKDOWN: { name: 'Markdown', extension: 'md' },
};

/** How to read the numbers beside results: decided automatically, or as the user says. */
export type NumberColumn = 'AUTO' | 'WEIGHTS' | 'DIE_ROLLS' | 'PART_OF_RESULT';
export const NUMBER_COLUMNS: Readonly<Record<NumberColumn, string>> = {
  AUTO: 'Detect automatically',
  WEIGHTS: 'Weights',
  DIE_ROLLS: 'Die rolls',
  PART_OF_RESULT: 'Part of the result text',
};

/** A row of a table, as the desktop's `RandomTableEntry` keeps it: text ("Result" when blank) and a weight of at least 1. */
export interface TableEntry {
  readonly text: string;
  readonly weight: number;
}

export function tableEntry(text: string | null | undefined, weight: number): TableEntry {
  return { text: text === null || text === undefined || javaIsBlank(text) ? 'Result' : text, weight: Math.max(1, weight) };
}

/** One table read from an import, not yet saved. */
export interface ParsedTable {
  readonly name: string;
  readonly description: string;
  readonly entries: readonly TableEntry[];
  readonly notes: readonly string[];
}

const parsedTable = (name: string, description: string, entries: TableEntry[], notes: string[]): ParsedTable => ({
  name: javaStrip(name),
  description: javaStrip(description),
  entries,
  notes,
});

export interface TableImportResult {
  readonly format: TableFormat;
  readonly tables: readonly ParsedTable[];
}

/** What's written out: a table's name, description and rows. */
export interface TableContent {
  readonly name: string;
  readonly description: string;
  readonly entries: readonly TableEntry[];
}

export const TABLE_FORMAT_ID = 'realmery-random-table';
export const BUNDLE_FORMAT_ID = 'realmery-random-tables';
const FORMAT_VERSION = 1;

const S = JS_SPACE;
const DIE_HEADER = /^\d*d(\d+|%)$/i;
const WEIGHT_HEADER = /^(weights?|chance|odds|freq|frequency|probability|%)$/i;
const ROLL_HEADER = /^(rolls?|range|die|dice|#|no\.?|number)$/i;
const RESULT_HEADER = /^(results?|text|entry|entries|outcome|name|item|items|description|encounters?|event)$/i;
const MARKDOWN_SEPARATOR = new RegExp(`^\\|?${S}*:?-{1,}:?${S}*(\\|${S}*:?-{1,}:?${S}*)*\\|?$`);
const NUMBERED_LINE = new RegExp(`^(\\d{1,4}(?:${S}*(?:-|–|—)${S}*\\d{1,4})?)(?:${S}*[.):|]${S}*|\\t${S}*|${S}+)(${JS_NOT_SPACE}.*)$`);
const BULLET = new RegExp(`^[-*•+]${S}+`);
const HEADING = new RegExp(`^#{1,6}${S}+(.*)$`);
const MARKDOWN_LINK = /\[([^\]]+)\]\([^)]*\)/g;
const MARKDOWN_STRONG = new RegExp(`(\\*\\*|__)(?=${JS_NOT_SPACE})(.+?)(?<=${JS_NOT_SPACE})\\1`, 'g');
const MARKDOWN_EMPHASIS = new RegExp(`(?<![\\w*])([*_])(?=[^ \\t\\n\\x0B\\f\\r*_])(.+?)(?<=[^ \\t\\n\\x0B\\f\\r*_])\\1(?![\\w*])`, 'g');
const MARKDOWN_CODE = /`([^`]+)`/g;

/** The desktop's `HtmlText.stripTags`: tags become spaces, a few entities are read, runs of space become one. */
export function stripTags(html: string): string {
  if (javaIsBlank(html)) return '';
  const unescaped = html
    .replace(/<[^>]+>/g, ' ')
    .replaceAll('&nbsp;', ' ')
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&quot;', '"')
    .replaceAll('&#39;', "'")
    .replaceAll('&amp;', '&');
  return javaStrip(unescaped.replace(new RegExp(`${S}+`, 'g'), ' '));
}

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

// ---- reading ----

/** Reads `text` as `format`, or as `detectFormat` decides when it's null; throws an InterchangeError if there's no table in it. */
export function readTables(text: string | null, format: TableFormat | null, numbers: NumberColumn | null, fallbackName: string | null): TableImportResult {
  let source = (text ?? '').replaceAll('\r\n', '\n').replaceAll('\r', '\n');
  if (source.startsWith('﻿')) source = source.slice(1);
  if (javaIsBlank(source)) throw new InterchangeError("There's nothing to import - the text is empty.");
  const actual = format ?? detectFormat(source);
  const mode = numbers ?? 'AUTO';
  const name = fallbackName === null || javaIsBlank(fallbackName) ? 'Imported Table' : fallbackName;
  const tables =
    actual === 'JSON' ? readJson(source, name) : actual === 'CSV' ? [readCsv(source, mode, name)] : actual === 'MARKDOWN' ? readMarkdown(source, mode, name) : [readText(source, mode, name)];
  if (tables.length === 0 || tables.every((table) => table.entries.length === 0)) {
    throw new InterchangeError(`No table rows were found in this ${TABLE_FORMATS[actual].name}.`);
  }
  return { format: actual, tables };
}

/** JSON if it starts like JSON, Markdown if it has a pipe-table separator row, CSV if every line has the same number of tabs, commas or semicolons, otherwise text. */
export function detectFormat(text: string): TableFormat {
  const trimmed = javaStrip(text);
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) return 'JSON';
  const lines = javaLines(trimmed).map(javaStrip).filter((line) => line !== '');
  if (lines.some((line) => line.includes('-') && line.includes('|') && MARKDOWN_SEPARATOR.test(line))) return 'MARKDOWN';
  for (const delimiter of ['\t', ',', ';']) {
    const first = countOutsideQuotes(lines[0] ?? '', delimiter);
    if (first > 0 && lines.every((line) => countOutsideQuotes(line, delimiter) === first)) return 'CSV';
  }
  return 'TEXT';
}

/** The format a file name implies, if it's one of these (`.tsv` counts as CSV). */
export function formatFromFileName(fileName: string): TableFormat | null {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.csv') || lower.endsWith('.tsv')) return 'CSV';
  if (lower.endsWith('.json')) return 'JSON';
  if (lower.endsWith('.md') || lower.endsWith('.markdown')) return 'MARKDOWN';
  if (lower.endsWith('.txt')) return 'TEXT';
  return null;
}

/** `"wandering-monsters_v2.csv"` → `"Wandering Monsters V2"`. */
export function nameFromFileName(fileName: string): string {
  let base = fileName.slice(Math.max(fileName.lastIndexOf('/'), fileName.lastIndexOf('\\')) + 1);
  const dot = base.lastIndexOf('.');
  if (dot > 0) base = base.slice(0, dot);
  const words = base
    .split(new RegExp(`[-_${'\\t\\n\\x0B\\f\\r '}]+`))
    .filter((w) => w !== '')
    // Character.toUpperCase: one character for one, so ß stays ß.
    .map((w) => {
      const upper = w[0]!.toUpperCase();
      return (upper.length === 1 ? upper : w[0]!) + w.slice(1);
    });
  return words.length === 0 ? 'Imported Table' : words.join(' ');
}

// -- JSON --

const isMap = (value: JavaJson | undefined): value is Map<string, JavaJson> => value instanceof Map;

function readJson(text: string, fallbackName: string): ParsedTable[] {
  const root = readJavaJson(text);
  const tables: ParsedTable[] = [];
  if (isMap(root)) {
    const list = root.get('tables');
    if (Array.isArray(list)) {
      let index = 1;
      for (const item of list) {
        if (isMap(item)) tables.push(tableFromJson(item, `${fallbackName} ${index}`));
        index++;
      }
    } else {
      tables.push(tableFromJson(root, fallbackName));
    }
  } else if (Array.isArray(root)) {
    const listOfTables = root.length > 0 && root.every((item) => isMap(item) && entriesOf(item) !== null);
    if (listOfTables) {
      let index = 1;
      for (const item of root) tables.push(tableFromJson(item as Map<string, JavaJson>, `${fallbackName} ${index++}`));
    } else {
      const notes: string[] = [];
      tables.push(parsedTable(fallbackName, '', jsonEntries(root, notes), notes));
    }
  } else {
    throw new InterchangeError("This JSON isn't a random table - expected an object or a list.");
  }
  return tables;
}

function tableFromJson(map: Map<string, JavaJson>, fallbackName: string): ParsedTable {
  const rawEntries = entriesOf(map);
  if (rawEntries === null) throw new InterchangeError('This JSON isn\'t a random table - it has no "entries" (or "results") list.');
  const notes: string[] = [];
  const format = map.get('format');
  if (format !== undefined && format !== null && format !== TABLE_FORMAT_ID && format !== BUNDLE_FORMAT_ID) {
    notes.push(`The file says its format is "${javaValueOf(format)}"; read it as a random table anyway.`);
  }
  const formula = map.get('formula');
  if (map.has('results') && formula !== undefined && formula !== null) notes.push(`Read as a virtual tabletop roll table (${javaValueOf(formula)}).`);
  const name = firstString(map, 'name', 'title');
  const description = javaStrip(stripTags(firstString(map, 'description', 'summary')));
  return parsedTable(javaIsBlank(name) ? fallbackName : name, description, jsonEntries(rawEntries, notes), notes);
}

function entriesOf(map: Map<string, JavaJson>): JavaJson[] | null {
  for (const key of ['entries', 'results', 'rows', 'items']) {
    const value = map.get(key);
    if (Array.isArray(value)) return value;
  }
  return null;
}

function jsonEntries(raw: readonly JavaJson[], notes: string[]): TableEntry[] {
  const entries: TableEntry[] = [];
  let skipped = 0;
  let clamped = 0;
  for (const item of raw) {
    let text: string;
    let weight = 1;
    if (isMap(item)) {
      text = javaStrip(stripTags(firstString(item, 'text', 'result', 'name', 'description', 'label')));
      const fromRange = rangeWeight(item.get('range'));
      const fromWeight = numberValue(item.get('weight'));
      const roll = item.get('roll');
      const fromRoll = roll === undefined || roll === null ? null : (RollRange.parse(javaValueOf(roll))?.size() ?? null);
      const chosen = fromRange ?? fromWeight ?? fromRoll;
      if (chosen !== null) weight = chosen;
    } else if (typeof item === 'string') {
      text = javaStrip(item);
    } else if (item === null) {
      text = '';
    } else {
      text = javaValueOf(item);
    }
    if (text === '') {
      skipped++;
      continue;
    }
    if (weight < 1) {
      clamped++;
      weight = 1;
    }
    entries.push(tableEntry(text, weight));
  }
  if (skipped > 0) notes.push(`Skipped ${plural(skipped, 'row')} with no result text.`);
  if (clamped > 0) notes.push(`${plural(clamped, 'row')} had a weight below 1; Realmery needs at least 1, so used 1.`);
  return entries;
}

function rangeWeight(range: JavaJson | undefined): number | null {
  if (Array.isArray(range) && range.length === 2) {
    const low = numberValue(range[0]);
    const high = numberValue(range[1]);
    if (low !== null && high !== null && high >= low) return javaToInt(high - low + 1);
  }
  return null;
}

/** `(int) Math.round(number.doubleValue())`, for a number or a number written as text. */
function numberValue(value: JavaJson | undefined): number | null {
  if (value instanceof JavaNumber) return javaToInt(javaRound(value.value));
  if (typeof value === 'string') {
    const parsed = javaParseDouble(javaStrip(value));
    return parsed === null ? null : javaToInt(javaRound(parsed));
  }
  return null;
}

function firstString(map: Map<string, JavaJson>, ...keys: string[]): string {
  for (const key of keys) {
    const value = map.get(key);
    if (value !== undefined && value !== null && !isMap(value) && !Array.isArray(value) && !javaIsBlank(javaValueOf(value))) return javaValueOf(value);
  }
  return '';
}

// -- CSV --

function readCsv(text: string, numbers: NumberColumn, name: string): ParsedTable {
  const delimiter = chooseDelimiter(text);
  const rows = parseCsv(text, delimiter).filter((row) => !row.every(javaIsBlank));
  const notes: string[] = [];
  let header: string[] | null = null;
  if (rows.length > 0 && looksLikeHeader(rows)) header = rows.shift()!;
  return parsedTable(name, '', buildEntries(header, rows, numbers, false, notes), notes);
}

function chooseDelimiter(text: string): string {
  const firstLine = javaLines(text).find((line) => !javaIsBlank(line)) ?? '';
  if (firstLine.includes('\t')) return '\t';
  return countOutsideQuotes(firstLine, ';') > countOutsideQuotes(firstLine, ',') ? ';' : ',';
}

function parseCsv(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (quoted) {
      if (c === '"') {
        if (i + 1 < text.length && text[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += c;
    } else if (c === '"' && javaIsBlank(cell)) {
      cell = '';
      quoted = true;
    } else if (c === delimiter) {
      row.push(javaStrip(cell));
      cell = '';
    } else if (c === '\n') {
      row.push(javaStrip(cell));
      cell = '';
      rows.push(row);
      row = [];
    } else cell += c;
  }
  if (cell !== '' || row.length > 0) {
    row.push(javaStrip(cell));
    rows.push(row);
  }
  return rows;
}

function looksLikeHeader(rows: readonly (readonly string[])[]): boolean {
  const first = rows[0]!;
  if (first.some((cell) => headerRole(cell) !== 'NONE')) return true;
  if (rows.length < 2 || first.length < 2) return false;
  return !first.some(isNumberish) && rows[1]!.some(isNumberish);
}

function countOutsideQuotes(line: string, target: string): number {
  let count = 0;
  let quoted = false;
  for (const c of line) {
    if (c === '"') quoted = !quoted;
    else if (c === target && !quoted) count++;
  }
  return count;
}

// -- Markdown --

function readMarkdown(text: string, numbers: NumberColumn, fallbackName: string): ParsedTable[] {
  const tables: ParsedTable[] = [];
  let heading: string | null = null;
  const prose: string[] = [];
  const looseLines: string[] = [];
  const tableLines: string[] = [];
  for (const rawLine of javaLines(`${text}\n`)) {
    const line = javaStrip(rawLine);
    if (line.startsWith('|')) {
      tableLines.push(line);
      continue;
    }
    if (tableLines.length > 0) {
      tables.push(markdownTable(tableLines, heading, prose, numbers, fallbackName, tables.length));
      tableLines.length = 0;
      prose.length = 0;
      heading = null;
    }
    const headingMatch = HEADING.exec(line);
    if (headingMatch) {
      heading = javaStrip(headingMatch[1]!);
      prose.length = 0;
    } else if (line !== '') {
      prose.push(line);
      looseLines.push(line);
    }
  }
  if (tableLines.length > 0) tables.push(markdownTable(tableLines, heading, prose, numbers, fallbackName, tables.length));
  if (tables.length === 0) {
    // No pipe table: a heading and a bullet or numbered list, which reads the same as plain text.
    const textBody = (heading === null ? '' : `# ${heading}\n`) + looseLines.join('\n');
    return [readText(textBody, numbers, fallbackName)];
  }
  return tables;
}

function markdownTable(lines: readonly string[], heading: string | null, prose: readonly string[], numbers: NumberColumn, fallbackName: string, index: number): ParsedTable {
  const rows = lines.filter((line) => !MARKDOWN_SEPARATOR.test(line)).map(splitMarkdownRow);
  const notes: string[] = [];
  const header = rows.length === 0 ? null : rows.shift()!;
  const name = heading !== null && !javaIsBlank(heading) ? heading : index === 0 ? fallbackName : `${fallbackName} ${index + 1}`;
  return parsedTable(name, javaStrip(prose.join(' ')), buildEntries(header, rows, numbers, false, notes), notes);
}

function splitMarkdownRow(line: string): string[] {
  let body = javaStrip(line);
  if (body.startsWith('|')) body = body.slice(1);
  if (body.endsWith('|') && !body.endsWith('\\|')) body = body.slice(0, -1);
  const cells: string[] = [];
  let cell = '';
  for (let i = 0; i < body.length; i++) {
    const c = body[i]!;
    if (c === '\\' && i + 1 < body.length && body[i + 1] === '|') {
      cell += '|';
      i++;
    } else if (c === '|') {
      cells.push(cleanMarkdownCell(cell));
      cell = '';
    } else cell += c;
  }
  cells.push(cleanMarkdownCell(cell));
  return cells;
}

/** Results are plain text: bold, italics, code and links (keeping their text) are dropped; `snake_case` survives. */
function cleanMarkdownCell(cell: string): string {
  let text = javaStrip(cell);
  text = text.replace(MARKDOWN_LINK, '$1');
  text = text.replace(MARKDOWN_STRONG, '$2');
  text = text.replace(MARKDOWN_EMPHASIS, '$2');
  text = text.replace(MARKDOWN_CODE, '$1');
  return javaStrip(text);
}

// -- plain text --

function readText(text: string, numbers: NumberColumn, fallbackName: string): ParsedTable {
  let name: string | null = null;
  const descriptionLines: string[] = [];
  const lines: string[] = [];
  for (const rawLine of javaLines(text)) {
    const line = javaStrip(rawLine);
    if (line === '') continue;
    if (line.startsWith('#')) {
      // Leading "#" lines are the table's name and description; later ones are comments.
      if (lines.length === 0) {
        const comment = line.replace(new RegExp(`^#+${S}*`), '');
        if (name === null) name = comment;
        else if (!javaIsBlank(comment)) descriptionLines.push(comment);
      }
      continue;
    }
    lines.push(line.replace(BULLET, ''));
  }
  const notes: string[] = [];
  const allNumbered = lines.length > 0 && numbers !== 'PART_OF_RESULT' && lines.every((line) => NUMBERED_LINE.test(line));
  const rows = lines.map((line) => {
    const match = NUMBERED_LINE.exec(line);
    return allNumbered && match ? [match[1]!, javaStrip(match[2]!)] : [line];
  });
  const tableName = name === null || javaIsBlank(name) ? fallbackName : name;
  return parsedTable(tableName, descriptionLines.join(' '), buildEntries(null, rows, numbers, true, notes), notes);
}

// -- shared row interpretation --

type HeaderRole = 'WEIGHT' | 'ROLL' | 'RESULT' | 'NONE';
type Reading = 'WEIGHTS' | 'ROLLS' | 'TEXT';

function headerRole(cell: string): HeaderRole {
  const text = javaStrip(cell);
  if (WEIGHT_HEADER.test(text)) return 'WEIGHT';
  if (ROLL_HEADER.test(text) || DIE_HEADER.test(text)) return 'ROLL';
  if (RESULT_HEADER.test(text)) return 'RESULT';
  return 'NONE';
}

/** Finds the number column, decides whether it holds weights or rolls, and joins the other cells into each result. */
function buildEntries(header: readonly string[] | null, rows: readonly (readonly string[])[], numbers: NumberColumn, fromText: boolean, notes: string[]): TableEntry[] {
  let columns = rows.reduce((max, row) => Math.max(max, row.length), 0);
  if (header !== null) columns = Math.max(columns, header.length);
  let numberIndex = -1;
  let headerReading: Reading | null = null;
  let headerDie = 0;
  if (header !== null) {
    for (let i = 0; i < header.length && numberIndex < 0; i++) {
      const role = headerRole(header[i]!);
      if (role === 'WEIGHT' || role === 'ROLL') {
        numberIndex = i;
        headerReading = role === 'WEIGHT' ? 'WEIGHTS' : 'ROLLS';
        const die = DIE_HEADER.exec(javaStrip(header[i]!));
        if (die) headerDie = die[1] === '%' ? 100 : Number(die[1]);
      }
    }
  }
  if (numberIndex < 0 && columns >= 2) {
    if (columnIsNumbers(rows, 0)) numberIndex = 0;
    else if (columnIsNumbers(rows, columns - 1)) numberIndex = columns - 1;
  }

  let reading: Reading;
  if (numberIndex < 0 || numbers === 'PART_OF_RESULT') reading = 'TEXT';
  else if (numbers === 'WEIGHTS') reading = 'WEIGHTS';
  else if (numbers === 'DIE_ROLLS') reading = 'ROLLS';
  else if (headerReading !== null) reading = headerReading;
  else reading = looksLikeRolls(rows, numberIndex) ? 'ROLLS' : 'WEIGHTS';

  const entries: TableEntry[] = [];
  const ranges: (RollRange | null)[] = [];
  const rawWeights: (number | null)[] = [];
  const texts: string[] = [];
  let unreadable = 0;
  let joinedColumns = 0;
  for (const row of rows) {
    const resultCells: string[] = [];
    row.forEach((cell, i) => {
      if (i === numberIndex && reading !== 'TEXT') return;
      if (!javaIsBlank(cell)) resultCells.push(javaStrip(cell));
    });
    const resultText =
      reading === 'TEXT' && numberIndex === 0 && resultCells.length > 1 ? `${resultCells[0]} ${resultCells.slice(1).join(' — ')}` : resultCells.join(' — ');
    joinedColumns = Math.max(joinedColumns, resultCells.length);
    if (javaIsBlank(resultText)) continue;
    const numberCell = numberIndex >= 0 && numberIndex < row.length ? row[numberIndex]! : '';
    if (reading === 'ROLLS') {
      const range = RollRange.parse(numberCell);
      if (range === null) unreadable++;
      ranges.push(range);
    } else if (reading === 'WEIGHTS') {
      const weight = parseWeight(numberCell);
      if (weight === null) unreadable++;
      rawWeights.push(weight);
    }
    texts.push(resultText);
  }

  const numberColumnName = fromText
    ? 'the numbers starting each line'
    : numberIndex === 0
      ? 'the first column'
      : numberIndex === columns - 1
        ? 'the last column'
        : `column ${numberIndex + 1}`;
  if (reading === 'ROLLS') {
    texts.forEach((text, i) => entries.push(tableEntry(text, ranges[i] === null ? 1 : ranges[i]!.size())));
    describeRolls(ranges, headerDie, numberColumnName, notes);
    if (unreadable > 0) notes.push(`${plural(unreadable, 'row')} had no readable roll; gave each a weight of 1.`);
  } else if (reading === 'WEIGHTS') {
    const weights = scaleWeights(rawWeights, notes);
    texts.forEach((text, i) => entries.push(tableEntry(text, weights[i]!)));
    notes.push(`Read ${numberColumnName} as weights.`);
    if (unreadable > 0) notes.push(`${plural(unreadable, 'row')} had no readable weight; used 1.`);
  } else {
    for (const text of texts) entries.push(tableEntry(text, 1));
    if (texts.length > 0) notes.push('Every result has the same chance (weight 1).');
  }
  if (joinedColumns > 1) notes.push(`Joined ${joinedColumns} columns into each result, separated by " — ".`);
  return entries;
}

function columnIsNumbers(rows: readonly (readonly string[])[], index: number): boolean {
  let any = false;
  for (const row of rows) {
    const cell = index < row.length ? row[index]! : '';
    if (javaIsBlank(cell)) continue;
    if (!isNumberish(cell)) return false;
    any = true;
  }
  return any;
}

const isNumberish = (cell: string) => RollRange.parse(cell) !== null || parseWeight(cell) !== null;

/** Ranges (`1-3`) or a plain count from 1 (`1, 2, 3...`) are rolls; anything else is weights. */
function looksLikeRolls(rows: readonly (readonly string[])[], index: number): boolean {
  const ranges: RollRange[] = [];
  for (const row of rows) {
    const cell = index < row.length ? row[index]! : '';
    if (javaIsBlank(cell)) continue;
    const range = RollRange.parse(cell);
    if (range === null) return false;
    ranges.push(range);
  }
  if (ranges.length === 0) return false;
  if (ranges.some((range) => range.size() > 1)) return true;
  for (let i = 0; i < ranges.length; i++) if (ranges[i]!.low !== i + 1) return false;
  return ranges.length > 1;
}

function describeRolls(ranges: readonly (RollRange | null)[], headerDie: number, columnName: string, notes: string[]): void {
  const known = ranges.filter((range): range is RollRange => range !== null).sort((a, b) => a.low - b.low);
  if (known.length === 0) return;
  const highest = known.reduce((max, range) => Math.max(max, range.high), 0);
  const lowest = known[0]!.low;
  const die = headerDie > 0 ? headerDie : highest;
  notes.push(`Read ${columnName} as d${die} rolls - each row's weight is how many numbers it covers.`);
  const gaps: string[] = [];
  const overlaps: string[] = [];
  let expected = lowest === 0 ? 0 : 1;
  for (const range of known) {
    if (range.low > expected) gaps.push(new RollRange(expected, range.low - 1).format(0));
    else if (range.low < expected) overlaps.push(range.format(0));
    expected = Math.max(expected, range.high + 1);
  }
  if (headerDie > 0 && expected <= headerDie) gaps.push(new RollRange(expected, headerDie).format(0));
  if (gaps.length > 0) notes.push(`No row covers ${gaps.join(', ')} - the table rolls as if those numbers weren't on the die.`);
  if (overlaps.length > 0) notes.push(`Some rolls are covered by more than one row (${overlaps.join(', ')}); each row still counts every number it lists.`);
  if (headerDie > 0 && highest > headerDie) notes.push(`The header says d${headerDie} but rows go up to ${highest}.`);
}

function parseWeight(cell: string): number | null {
  let text = javaStrip(cell).replaceAll('%', '').replaceAll(',', '.');
  if (text.endsWith('x') || text.endsWith('×')) text = javaStrip(text.slice(0, -1));
  if (text === '') return null;
  const value = javaParseDouble(text);
  return value !== null && Number.isFinite(value) ? value : null;
}

/** Weights must be whole numbers of at least 1: decimals are scaled up by one factor to keep the odds, and anything below 1 becomes 1. */
function scaleWeights(raw: readonly (number | null)[], notes: string[]): number[] {
  let factor = 1;
  while (factor < 1000 && !raw.every((w) => w === null || Math.abs(w * factor - javaRint(w * factor)) < 1e-9)) factor *= 10;
  let weights: number[] = [];
  let clamped = 0;
  for (const weight of raw) {
    let value = weight === null ? 1 : javaRound(weight * factor);
    if (value < 1) {
      clamped++;
      value = 1;
    }
    weights.push(javaToInt(Math.min(value, 1_000_000)));
  }
  if (factor > 1) {
    const divisor = weights.reduce((a, b) => gcd(a, b), 0);
    if (divisor > 1) weights = weights.map((w) => Math.trunc(w / divisor));
    notes.push('Some weights had decimals; scaled every weight up by the same amount to keep the odds.');
  }
  if (clamped > 0) notes.push(`${plural(clamped, 'row')} had a weight below 1; Realmery needs at least 1, so used 1.`);
  return weights;
}

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

// ---- writing ----

/** `table` in `format`, which reads back with the same entries, name and description. */
export function writeTable(table: TableContent, format: TableFormat): string {
  switch (format) {
    case 'JSON':
      return writeJson(new Map<string, JsonValue>([['format', TABLE_FORMAT_ID], ['version', FORMAT_VERSION], ...tableJson(table)]));
    case 'CSV':
      return writeCsv(table);
    case 'TEXT':
      return writeText(table);
    case 'MARKDOWN':
      return writeMarkdown(table);
  }
}

/** Several tables in one file: only JSON (a bundle) and Markdown can hold more than one. */
export function writeTables(tables: readonly TableContent[], format: TableFormat): string {
  if (format === 'JSON') {
    return writeJson(
      new Map<string, JsonValue>([
        ['format', BUNDLE_FORMAT_ID],
        ['version', FORMAT_VERSION],
        ['tables', tables.map((table) => new Map<string, JsonValue>(tableJson(table)))],
      ]),
    );
  }
  if (format === 'MARKDOWN') return tables.map(writeMarkdown).join('\n');
  throw new RangeError(`${TABLE_FORMATS[format].name} can only hold one table`);
}

function tableJson(table: TableContent): [string, JsonValue][] {
  const entries: [string, JsonValue][] = [
    ['name', table.name],
    ['description', table.description],
  ];
  const total = totalWeight(table);
  if (total > 0) entries.push(['die', `d${total}`]);
  const ranges = rolls(table);
  entries.push([
    'entries',
    table.entries.map(
      (entry, i) =>
        new Map<string, JsonValue>([
          ['roll', ranges[i]!.format(0)],
          ['weight', entry.weight],
          ['text', entry.text],
        ]),
    ),
  ]);
  return entries;
}

function writeCsv(table: TableContent): string {
  return `weight,result\n${table.entries.map((entry) => `${entry.weight},${csvCell(entry.text)}\n`).join('')}`;
}

function csvCell(text: string): string {
  const flat = text.replaceAll('\r', '');
  if (flat.includes(',') || flat.includes('"') || flat.includes('\n') || flat !== javaStrip(flat)) return `"${flat.replaceAll('"', '""')}"`;
  return flat;
}

function writeText(table: TableContent): string {
  let out = `# ${oneLine(table.name)}\n`;
  for (const line of javaLines(table.description)) {
    if (!javaIsBlank(line)) out += `# ${javaStrip(line)}\n`;
  }
  // Weight-1 rows need no numbers - unless a result itself starts with one, which would read
  // back as a roll. Numbers go on every line or none, since that's how they're read.
  const numbered = table.entries.some((entry) => entry.weight !== 1 || NUMBERED_LINE.test(oneLine(entry.text)));
  const ranges = rolls(table);
  table.entries.forEach((entry, i) => {
    out += `${numbered ? `${ranges[i]!.format(0)}\t` : ''}${oneLine(entry.text)}\n`;
  });
  return out;
}

function writeMarkdown(table: TableContent): string {
  const total = totalWeight(table);
  const width = total === 100 ? 2 : 0;
  let out = `# ${oneLine(table.name)}\n\n`;
  if (!javaIsBlank(table.description)) out += `${oneLine(table.description)}\n\n`;
  out += `| d${total} | Result |\n| --- | --- |\n`;
  const ranges = rolls(table);
  table.entries.forEach((entry, i) => {
    let roll = ranges[i]!.format(width);
    if (total === 100) roll = roll.replaceAll('100', '00');
    out += `| ${roll} | ${oneLine(entry.text).replaceAll('|', '\\|')} |\n`;
  });
  return out;
}

/** Each entry's weight laid out as consecutive rolls on a die the size of the total weight. */
function rolls(table: TableContent): RollRange[] {
  let next = 1;
  return table.entries.map((entry) => {
    const range = new RollRange(next, next + entry.weight - 1);
    next += entry.weight;
    return range;
  });
}

const totalWeight = (table: TableContent) => table.entries.reduce((sum, entry) => sum + entry.weight, 0);

const oneLine = (text: string) => javaStrip(text.replace(new RegExp(`${S}*${JAVA_LINE_BREAK}${S}*`, 'g'), ' '));
